import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import img from '../image/EEKANSHI INDOOR-2.png'

export const Header = ({ onQuoteClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Services', path: '/services' },
    { name: 'Why Choose Us', path: '/why-choose-us' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/98 backdrop-blur-xl shadow-lg border-b border-gray-100'
          : 'bg-white/80 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Enhanced Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative w-12 h-12 m-2   rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              {/* <span className="text-white font-bold text-2xl"></span> */}
              <img src={img} className='rounded-2xl h-15px' alt="" />
              
              <div className="absolute inset-0 bg-gradient-to-br  to-transparent rounded-xl"></div>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xl text-gray-900 leading-tight tracking-tight group-hover:text-blue-900 transition-colors">
                Ekaanshi Indoor
              </span>
              <span className="text-xs text-gray-500 font-medium">Interiors & Decor Solutions</span>
            </div>
          </Link>

          {/* Enhanced Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-5 py-2.5 text-sm font-semibold transition-all duration-300 group ${
                  location.pathname === link.path
                    ? 'text-blue-900'
                    : 'text-gray-700 hover:text-blue-900'
                }`}
              >
                {link.name}
                {/* Animated underline */}
                <span
                  className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-900 transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'opacity-100 scale-x-100'
                      : 'opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100'
                  }`}
                ></span>
              </Link>
            ))}
          </nav>

          {/* Enhanced CTA Section */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Phone Button */}
            <a
              href="tel:+917017168371"
              className="flex items-center space-x-2 px-4 py-2.5 text-blue-900 hover:bg-blue-50 rounded-lg transition-all duration-300 group"
            >
              <Phone size={18} className="group-hover:scale-110 transition-transform" />
              <span className="text-sm font-semibold">Call Us</span>
            </a>
            
            {/* Enhanced Quote Button */}
            <Button
              onClick={onQuoteClick}
              className="relative bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 overflow-hidden group"
            >
              <span className="relative z-10">Get a Quote</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Button>
          </div>

          {/* Enhanced Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-3 rounded-xl text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-all duration-300"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Enhanced Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-6 border-t border-gray-200 bg-white/95 backdrop-blur-md">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-4 rounded-xl text-base font-semibold transition-all duration-300 transform ${
                    location.pathname === link.path
                      ? 'text-blue-900 bg-blue-50 shadow-sm'
                      : 'text-gray-700 hover:text-blue-900 hover:bg-blue-50 hover:translate-x-1'
                  }`}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Actions */}
              <div className="pt-4 space-y-3">
                <a
                  href="tel:+917017168371"
                  className="flex items-center justify-center space-x-2 px-4 py-4 text-blue-900 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-300 font-semibold"
                >
                  <Phone size={20} />
                  <span>Call Us Now</span>
                </a>
                <Button
                  onClick={() => {
                    onQuoteClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white font-semibold py-4 rounded-xl shadow-lg"
                >
                  Get a Quote
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};